import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/web'
import { semanticColors } from '@common/ui'
import { alpha } from '@common/ui/mantine'
import ReactCodeMirror, { EditorView, ReactCodeMirrorProps, highlightWhitespace } from '@uiw/react-codemirror'
import { createTheme } from '@uiw/codemirror-themes'
import { tags } from '@lezer/highlight'
import type { JSONSchema7 } from 'json-schema'
import { yamlSchema } from 'codemirror-json-schema/yaml'
import { jsonSchema as jsonSchemaExt } from 'codemirror-json-schema'
import { parseAssetReference } from 'runtimes-shared'
import {
  assetReferenceExtensions,
  configureAssetReferences,
  type AssetChipColors,
  type AssetTriggerRange,
} from '@common/asset-references'
import { getLanguageExtensions, LanguageSelect, useLanguageDetection } from './LanguageSelect'
import useAssetReferenceLabelResolver from './useAssetReferenceLabelResolver'
import AssetReferencePicker from './AssetReferencePicker'
import classes from './PayloadEditor.module.css'
import entityIdFromString = com.eidu.entity.entityIdFromString
import EntityId = com.eidu.entity.EntityId

// The editor renders in the app's light surface (matching the previous MUI light theme).
const sc = semanticColors.light

type PayloadEditorProps = {
  value?: string
  onChange?: (value: string) => void
  editable?: boolean
  placeholder?: string
  jsonSchema?: Record<string, unknown>
}

const PayloadEditor = ({ value, onChange, editable = true, placeholder, jsonSchema }: PayloadEditorProps) => {
  const { resolvedLabels: resolvedAssetLabels } = useAssetReferenceLabelResolver(value ?? '')
  const { activeLanguage, selectActiveLanguage } = useLanguageDetection(value)

  const [assetTriggerRange, setAssetTriggerRange] = useState<AssetTriggerRange | undefined>(undefined)
  const [pickerEntityId, setPickerEntityId] = useState<EntityId | undefined>(undefined)
  const [pickerVersionId, setPickerVersionId] = useState<string | undefined>(undefined)

  const cmTheme = useMemo(
    () =>
      createTheme({
        theme: 'light',
        settings: {
          background: sc.surface,
          foreground: sc.textPrimary,
          selection: sc.surfaceVariant,
          gutterBackground: sc.surfaceVariant,
          gutterForeground: sc.textDisabled,
          lineHighlight: sc.surfaceVariant,
        },
        styles: [
          { tag: tags.keyword, color: sc.primary },
          { tag: tags.string, color: sc.success },
          { tag: tags.number, color: sc.warning },
          { tag: tags.comment, color: sc.textDisabled },
          { tag: tags.propertyName, color: sc.accent },
        ],
      }),
    []
  )

  const whitespaceStyle = useMemo(
    () =>
      EditorView.theme({
        '.cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, #f5f5f5 20%, transparent 5%)',
        },
        '.cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="%23f5f5f5" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
        '.cm-activeLine .cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, white 20%, transparent 5%)',
        },
        '.cm-activeLine .cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="white" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
        '.cm-selectionBackground .cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, white 20%, transparent 5%)',
        },
        '.cm-selectionBackground .cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="white" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
      }),
    []
  )

  const assetChipColors = useMemo<AssetChipColors>(
    () => ({
      chipBackground: alpha(sc.primary, 0.12),
      chipForeground: sc.primaryActive,
      chipHoverBackground: alpha(sc.primary, 0.2),
      editBackground: sc.surface,
      editForeground: sc.primaryActive,
      editBorder: alpha(sc.primary, 0.4),
      editHoverBackground: alpha(sc.primary, 0.08),
      editHoverBorder: sc.primary,
    }),
    []
  )

  const hrefForAsset = useCallback(
    ({ uuid, typeId, versionId }: { uuid: string; typeId?: string; versionId?: string }) =>
      typeId ? `/entities/${typeId}/edit/${uuid}${versionId ? `?versionId=${versionId}` : ''}` : undefined,
    []
  )

  const viewRef = useRef<EditorView | null>(null)

  const onAssetTrigger = useCallback((range: AssetTriggerRange) => {
    setAssetTriggerRange(range)
    const view = viewRef.current
    if (view) {
      const text = view.state.doc.sliceString(range.from, range.to)
      const parsed = parseAssetReference(text)
      setPickerEntityId(parsed ? entityIdFromString(parsed.entityId) ?? undefined : undefined)
      setPickerVersionId(parsed?.versionId)
    } else {
      setPickerEntityId(undefined)
      setPickerVersionId(undefined)
    }
  }, [])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    configureAssetReferences(view, {
      labels: resolvedAssetLabels ?? new Map(),
      editable,
      onEditAsset: onAssetTrigger,
    })
  }, [resolvedAssetLabels, editable, onAssetTrigger])

  const schemaExtensions = useMemo(() => {
    if (!jsonSchema) return []
    const schema = jsonSchema as JSONSchema7
    if (activeLanguage === 'yaml') return yamlSchema(schema)
    if (activeLanguage === 'json') return jsonSchemaExt(schema)
    return []
  }, [jsonSchema, activeLanguage])

  const extensions = useMemo(
    () => [
      ...(schemaExtensions.length > 0 ? [] : getLanguageExtensions(activeLanguage)),
      ...schemaExtensions,
      EditorView.lineWrapping,
      highlightWhitespace(),
      whitespaceStyle,
      ...assetReferenceExtensions({
        labels: resolvedAssetLabels ?? new Map(),
        editable,
        onEditAsset: onAssetTrigger,
        colors: assetChipColors,
        href: hrefForAsset,
      }),
    ],
    [activeLanguage, whitespaceStyle, schemaExtensions]
  )

  const handlePickerConfirm = useCallback(() => {
    const view = viewRef.current
    if (!view || !assetTriggerRange || !pickerEntityId) return
    const replacement = `asset://${pickerEntityId.toString()}${pickerVersionId ? `@${pickerVersionId}` : ''}`
    view.dispatch({ changes: { from: assetTriggerRange.from, to: assetTriggerRange.to, insert: replacement } })
    setAssetTriggerRange(undefined)
    setPickerEntityId(undefined)
    setPickerVersionId(undefined)
  }, [assetTriggerRange, pickerEntityId, pickerVersionId])

  const handlePickerCancel = useCallback(() => {
    setAssetTriggerRange(undefined)
    setPickerEntityId(undefined)
    setPickerVersionId(undefined)
  }, [])

  const props: ReactCodeMirrorProps = {
    value,
    onChange,
    editable,
    placeholder,
    height: '100%',
    theme: cmTheme,
    extensions,
    basicSetup: {
      lineNumbers: true,
      foldGutter: true,
      highlightActiveLine: editable,
    },
    style: { flex: 1, minHeight: 0, overflow: 'auto' },
  }

  return (
    <div className={classes.editorFrame} data-editable={editable}>
      <LanguageSelect value={activeLanguage} onChange={selectActiveLanguage} />
      <ReactCodeMirror
        {...props}
        data-testid="payload-editor"
        onCreateEditor={(view) => {
          viewRef.current = view
        }}
      />
      {assetTriggerRange && (
        <AssetReferencePicker
          open
          value={pickerEntityId}
          versionId={pickerVersionId}
          onChange={(next) => {
            setPickerEntityId(next)
            if (!next || next.toString() !== pickerEntityId?.toString()) setPickerVersionId(undefined)
          }}
          onVersionChange={setPickerVersionId}
          onConfirm={handlePickerConfirm}
          onCancel={handlePickerCancel}
        />
      )}
    </div>
  )
}

export default PayloadEditor
